import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

interface PopoverContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  contentRef: React.RefObject<HTMLElement | null>;
}

const PopoverContext = React.createContext<PopoverContextValue | null>(null);

export function Popover({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLElement | null>(null);
  const contentRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (contentRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <PopoverContext.Provider value={{ open, setOpen, anchorRef, contentRef }}>
      {children}
    </PopoverContext.Provider>
  );
}

export function PopoverTrigger({
  children,
  asChild,
  className,
  ...props
}: React.HTMLAttributes<HTMLElement> & { asChild?: boolean }) {
  const ctx = React.useContext(PopoverContext);
  if (!ctx) return null;

  const handleClick = (e: React.MouseEvent) => {
    (ctx.anchorRef as React.MutableRefObject<HTMLElement | null>).current = e.currentTarget as HTMLElement;
    ctx.setOpen(!ctx.open);
    (props as React.ButtonHTMLAttributes<HTMLButtonElement>).onClick?.(e);
  };

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<{ onClick?: (e: React.MouseEvent) => void; ref?: React.Ref<HTMLElement> }>, {
      ref: (el: HTMLElement | null) => {
        (ctx.anchorRef as React.MutableRefObject<HTMLElement | null>).current = el;
        const childRef = (children as React.ReactElement<{ ref?: React.Ref<HTMLElement> }>).ref;
        if (typeof childRef === 'function') childRef(el);
        else if (childRef) (childRef as React.MutableRefObject<HTMLElement | null>).current = el;
      },
      onClick: (e: React.MouseEvent) => {
        handleClick(e);
        (children as React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>).props.onClick?.(e);
      },
    });
  }

  return (
    <div
      ref={ctx.anchorRef as React.RefObject<HTMLDivElement>}
      role="button"
      tabIndex={0}
      className={cn('cursor-pointer', className)}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick(e as unknown as React.MouseEvent);
        }
      }}
      {...props}
    >
      {children}
    </div>
  );
}

export function PopoverContent({
  children,
  className,
  id,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { id?: string }) {
  const ctx = React.useContext(PopoverContext);
  if (!ctx || !ctx.open) return null;

  const anchor = ctx.anchorRef.current;
  if (!anchor) return null;

  const content = (
    <div
      ref={ctx.contentRef}
      id={id}
      className={cn(
        'fixed left-1/2 top-1/2 z-50 min-w-[12rem] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-md border bg-background p-2 shadow-lg',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );

  return createPortal(content, document.body);
}
